/*
 * SBVTools US. Find Your Bike. Shared source of truth (Shopify build).
 *
 * ONE data module, loaded by BOTH the hero make picker
 * (sections/fyb-hero-picker.liquid) and the finder page
 * (sections/fyb-finder.liquid). Do NOT duplicate MODELS or RESULTS in
 * section markup. Reference window.SBV_FYB.
 *
 * Ported from sbv-prototype/v4-inverted/assets/find-your-bike.js. The one
 * change for Shopify: RESULTS[*].cta and RESULTS[*].pro now point at real
 * storefront paths (/products/<handle> or /collections/<handle>) instead
 * of the prototype static .html files. The handles below are PLACEHOLDERS
 * until they are mapped to the imported catalog (see build note). Every
 * placeholder is the value of a `handle` field so a single find-replace
 * maps them later. No product data is invented here: only links.
 *
 * Make keys are short slugs (bmw, harley, ktm, ducati, honda, aprilia,
 * re_hdpa, expert). A finder <option value="..."> must match a key for the
 * cascade to populate the Model list.
 *
 * 52536 dual-segment: Royal Enfield AND HD Revolution Max (Pan America /
 * Sportster S / Nightster) share a single Add-On, grouped under re_hdpa.
 */
(function (global) {
  'use strict';

  // Already loaded once (both sections may sit on one page). Idempotent.
  if (global.SBV_FYB) return;

  // PLACEHOLDER product handles. Map these to the real imported catalog.
  // Single source so the finder and any future surface resolve identically.
  /*
   * Handles are per BIKE, not per make-group, because the catalog is per bike.
   * The Bundle Composition tab gives one row per kit, and two of those rows
   * carry an Extra Part that no other kit has: Triumph (52527) and KOVE
   * (52257). A group-level handle sent those riders to a kit missing their
   * own part, so every model now resolves to the kit its own row specifies.
   * See MODEL_RESULTS below.
   *
   * Kits still unpublished fall back to basic_set: their Add-On is at zero
   * stock, so there is no page to send anyone to. Never link a dead handle.
   */
  var HANDLES = {
    kit_ajp:            'sbv-ajp-tool-set',
    pro_ajp:            'sbv-ajp-pro-pack',
    kit_aprilia:        'sbv-aprilia-tool-set',
    pro_aprilia:        'sbv-aprilia-pro-pack',
    kit_bmw:            'sbv-bmw-tool-set',
    pro_bmw:            'sbv-bmw-pro-pack',
    kit_cfmoto:         'sbv-basic-tool-set',
    kit_ducati:         'sbv-ducati-tool-set',
    pro_ducati:         'sbv-ducati-pro-pack',
    kit_gasgas:         'sbv-gasgas-tool-set',
    pro_gasgas:         'sbv-gasgas-pro-pack',
    kit_harley:         'sbv-harley-davidson-tool-set',
    pro_harley:         'sbv-harley-davidson-touring-pro-pack',
    kit_honda:          'sbv-honda-tool-set',
    pro_honda:          'sbv-honda-pro-pack',
    kit_husqvarna:      'sbv-husqvarna-tool-set',
    pro_husqvarna:      'sbv-husqvarna-pro-pack',
    kit_indian:         'sbv-indian-tool-set',
    pro_indian:         'sbv-indian-pro-pack',
    kit_kawasaki:       'sbv-kawasaki-tool-set',
    pro_kawasaki:       'sbv-kawasaki-pro-pack',
    kit_kove:           'sbv-kove-tool-set',
    pro_kove:           'sbv-kove-pro-pack',
    kit_ktm:            'sbv-ktm-tool-set',
    pro_ktm:            'sbv-ktm-pro-pack',
    kit_royalenfield:   'sbv-basic-tool-set',
    kit_suzuki:         'sbv-basic-tool-set',
    kit_triumph:        'sbv-triumph-tool-set',
    pro_triumph:        'sbv-triumph-pro-pack',
    kit_voge:           'sbv-voge-tool-set',
    pro_voge:           'sbv-voge-pro-pack',
    kit_yamaha:         'sbv-yamaha-tool-set',
    pro_yamaha:         'sbv-yamaha-pro-pack',
    basic_set:          'sbv-basic-tool-set'
  };

  function productHref(handle) {
    return '/products/' + handle;
  }

  var MAKES = [
    { value: '', label: 'Select Make' },
    { value: 'ajp', label: 'AJP' },
    { value: 'aprilia', label: 'Aprilia' },
    { value: 'bmw', label: 'BMW' },
    { value: 'cfmoto', label: 'CFMOTO' },
    { value: 'ducati', label: 'Ducati' },
    { value: 'gasgas', label: 'GasGas' },
    { value: 'harley', label: 'Harley-Davidson' },
    { value: 'honda', label: 'Honda' },
    { value: 'husqvarna', label: 'Husqvarna' },
    { value: 'indian', label: 'Indian' },
    { value: 'kawasaki', label: 'Kawasaki' },
    { value: 'kove', label: 'KOVE' },
    { value: 'ktm', label: 'KTM' },
    { value: 'royalenfield', label: 'Royal Enfield' },
    { value: 'suzuki', label: 'Suzuki' },
    { value: 'triumph', label: 'Triumph' },
    { value: 'voge', label: 'VOGE' },
    { value: 'yamaha', label: 'Yamaha' }
  ];

  /*
   * Hero make picker. Single make selection only, no model. Listed as
   * individual makes to match the SBVTools storefront. `value` resolves
   * through resolveMakeSlug() so the destination finder maps to the right
   * Add-On. `logo` is optional: when set to a theme asset filename the
   * picker renders the brand mark in front of the name. Left empty here
   * (text only) until brand-logo assets are mapped. See build note.
   */
  var HERO_MAKES = [
    { value: 'ajp', label: 'AJP', logo: '' },
    { value: 'aprilia', label: 'Aprilia', logo: '' },
    { value: 'bmw', label: 'BMW', logo: '' },
    { value: 'cfmoto', label: 'CFMOTO', logo: '' },
    { value: 'ducati', label: 'Ducati', logo: '' },
    { value: 'gasgas', label: 'GasGas', logo: '' },
    { value: 'harley', label: 'Harley-Davidson', logo: '' },
    { value: 'honda', label: 'Honda', logo: '' },
    { value: 'husqvarna', label: 'Husqvarna', logo: '' },
    { value: 'indian', label: 'Indian', logo: '' },
    { value: 'kawasaki', label: 'Kawasaki', logo: '' },
    { value: 'kove', label: 'KOVE', logo: '' },
    { value: 'ktm', label: 'KTM', logo: '' },
    { value: 'royalenfield', label: 'Royal Enfield', logo: '' },
    { value: 'suzuki', label: 'Suzuki', logo: '' },
    { value: 'triumph', label: 'Triumph', logo: '' },
    { value: 'voge', label: 'VOGE', logo: '' },
    { value: 'yamaha', label: 'Yamaha', logo: '' }
  ];

  var MODELS = {
    ajp: [
      'PR7',
      'SPR 125',
      'SPR 240',
      'SPR 240 X',
      'SPR 250',
      'SPR 310 R',
      'SPR 510 R'
    ],
    aprilia: [
      'Tuareg 660',
      'Tuareg 660 Rally',
      'RS 457',
      'Tuono 457',
      'RS 660',
      'RS 660 Factory',
      'Tuono 660',
      'Tuono 660 Factory'
    ],
    bmw: [
      'R NineT',
      'R1150 RT',
      'R12/R12S/R12 G/S',
      'R1200GS/GSA (Oil-cooled)',
      'R1200RT',
      'K1200GT/R/S',
      'R1200GS/GSA LC',
      'R1250GS/GSA',
      'R1250RS',
      'R1250RT',
      'R1300GS/GSA',
      'R1300RS',
      'R1300RT',
      'K1300GT/R/S',
      'K1600GT/GTL/B',
      'R18',
      'R18 Transcontinental',
      'F750GS',
      'F800GS (2024-present)',
      'F850GS/GSA',
      'F900GS/GSA',
      'F900R/XR',
      'M1000RR/R/XR',
      'S1000RR/R/XR',
      'F650GS',
      'G650GS',
      'F700GS',
      'F800GS (2008-2019)',
      'F800R'
    ],
    cfmoto: [
      '450MT-X',
      '800MT-X',
      '1000MT-X'
    ],
    ducati: [
      'DesertX',
      'Multistrada V2',
      'Multistrada V4'
    ],
    gasgas: [
      'MC 450F',
      'ES 700',
      'SM 700'
    ],
    harley: [
      'Breakout',
      'Deluxe',
      'Electra Glide',
      'Fat Bob',
      'Fat Boy',
      'Heritage Classic',
      'Low Rider',
      'Low Rider ST/S',
      'Night Rod',
      'Road Glide',
      'Road King',
      'Slim',
      'Sportster XL',
      'Street Bob',
      'Street Glide',
      'Street Rod',
      'Super Glide',
      'Tri Glide Ultra / Ultra Classic',
      'Ultra Limited',
      'V-Rod',
      'V-Rod Muscle',
      'Wide Glide',
      'Pan America 1250/Special/ST',
      'Sportster S',
      'Nightster/Special'
    ],
    honda: [
      'CB650R/F',
      'NC750X',
      'Shadow 750 (Phantom)',
      'XL750 Transalp',
      'CRF1000 Africa Twin',
      'CRF1100 Africa Twin',
      'Rebel 1100',
      'NT1100',
      'VTX1300/VTX1800',
      'CRF250L/CRF250L Rally',
      'CRF300L/CRF300L Rally',
      'CRF450L/CRF450RL'
    ],
    husqvarna: [
      'FC 125/250/350/450',
      'FE 250/350/450/501',
      'TE 250i/300i',
      'Vitpilen 401/701',
      '701 Enduro/Supermoto',
      'Norden 901'
    ],
    indian: [
      'Chieftain',
      'FTR 1200',
      'Scout Bobber'
    ],
    kawasaki: [
      'Ninja ZX-6R',
      'KLR650',
      'Versys 650',
      'Versys 1000',
      'Vulcan S 650',
      'Z900/RS',
      'Vulcan 900/1700/Vaquero/Voyager',
      'Concours 14 (GTR1400)'
    ],
    kove: [
      '450 Rally',
      '800X Rally/Pro'
    ],
    ktm: [
      '125 EXC',
      '250/350/450 SX-F',
      '250/300 EXC TPI',
      '390 Adventure',
      '390 Duke',
      'RC 390',
      '450/500 EXC-F',
      '640 Adventure/LC4',
      '690 Enduro R/SMC R',
      '790 Adventure',
      '890 Adventure/R',
      '950 Adventure',
      '950 Supermoto',
      '990 Adventure',
      '990 Supermoto/SMR/SMT',
      '1290 Super Adventure S/R',
      '1290 Super Duke R'
    ],
    royalenfield: [
      'Bullet 350/500',
      'Hunter 350',
      'Meteor 350',
      'Classic 350 (J-Series)',
      'Classic 350 (UCE)',
      'Himalayan 411',
      'Scram 411',
      'Himalayan 450',
      'Continental GT 650',
      'Interceptor 650',
      'Shotgun 650',
      'Super Meteor 650'
    ],
    suzuki: [
      'DR-Z400S / SM',
      'V-Strom 650',
      'V-Strom 1000/1050',
      'Hayabusa',
      'GSX-R600',
      'GSX-S750',
      'V-Strom 800DE'
    ],
    triumph: [
      'Trident 660',
      'Daytona 675/Moto2 765',
      'Street Triple 675/765',
      'Tiger 800/XR/XC',
      'Tiger 850 Sport',
      'Tiger 900/GT/Rally',
      'Thruxton 900/1200',
      'Speed Triple 1050/1200',
      'Speed Twin 1200',
      'Scrambler 900/1200',
      'Bonneville T100/T120',
      'Tiger 1200 Explorer/GT/Rally (2012-2021)',
      'Rocket III/3 R/GT',
      'Tiger 1200 (2022-present)'
    ],
    voge: [
      'DS625X',
      'DS800X Rally',
      'DS900X'
    ],
    yamaha: [
      'XSR125',
      'YZF-R125',
      'WR250R/WR250X',
      'MT-03',
      'R3',
      'TMAX 530/560',
      'XT660Z Tenere',
      'MT-07/FZ-07/XSR700',
      'Tracer 700',
      'Tenere 700',
      'R7',
      'MT-09/FZ-09/XSR900',
      'Tracer 900/9 GT',
      'FJ09',
      'Bolt 950',
      'Super Tenere 1200'
    ]
  };

  /*
   * Per-make result. `cta` is the primary kit link, `pro` is the secondary
   * Pro Set link. Both are real storefront paths built from placeholder
   * handles. The Pro Set is a configure-by-bike bundle (FYB drives it), so
   * `pro` points at the single Pro Set surface for now; per-bike Pro Set
   * routing is a separate decision (see build note).
   */
  var RESULTS = {
    ajp: {
      title: 'AJP Motorcycle Tool Set (SKU 52504-AJP), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_ajp), pro: productHref(HANDLES.pro_ajp)
    },
    aprilia: {
      title: 'Aprilia Tuareg 660 Motorcycle Tool Set (SKU 52609), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_aprilia), pro: productHref(HANDLES.pro_aprilia)
    },
    bmw: {
      title: 'BMW Motorcycle Tool Set (SKU 52502N), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_bmw), pro: productHref(HANDLES.pro_bmw)
    },
    cfmoto: {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The CFMOTO Add-On Pouch is out of stock. This is the Primary Tool Set it builds on.',
      cta: productHref(HANDLES.kit_cfmoto)
    },
    ducati: {
      title: 'Ducati DesertX, Multistrada V2 & V4 Motorcycle Tool Set (SKU 52608), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_ducati), pro: productHref(HANDLES.pro_ducati)
    },
    gasgas: {
      title: 'GasGas Motorcycle Tool Set (SKU 52603-GG), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_gasgas), pro: productHref(HANDLES.pro_gasgas)
    },
    harley: {
      title: 'Harley-Davidson Dyna, Softail, Sportster XL, Touring, V-Rod & CVO Motorcycle Tool Set (SKU 52507), $329.',
      body: 'One roll built for these Harley-Davidson models. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_harley), pro: productHref(HANDLES.pro_harley)
    },
    honda: {
      title: 'Honda Motorcycle Tool Set (SKU 52504), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_honda), pro: productHref(HANDLES.pro_honda)
    },
    husqvarna: {
      title: 'Husqvarna Motorcycle Tool Set (SKU 52603-HQV), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_husqvarna), pro: productHref(HANDLES.pro_husqvarna)
    },
    indian: {
      title: 'Indian Motorcycle Tool Set (SKU 52504-IND), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_indian), pro: productHref(HANDLES.pro_indian)
    },
    kawasaki: {
      title: 'Kawasaki Motorcycle Tool Set (SKU 52504-KAW), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_kawasaki), pro: productHref(HANDLES.pro_kawasaki)
    },
    kove: {
      title: 'KOVE Motorcycle Tool Set (SKU 52603-KOV), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_kove), pro: productHref(HANDLES.pro_kove)
    },
    ktm: {
      title: 'KTM Motorcycle Tool Set (SKU 52603-KTM), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_ktm), pro: productHref(HANDLES.pro_ktm)
    },
    royalenfield: {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The Primary Tool Set, our complete kit for Royal Enfield riders.',
      cta: productHref(HANDLES.kit_royalenfield)
    },
    suzuki: {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The Suzuki Add-On Pouch is out of stock. This is the Primary Tool Set it builds on.',
      cta: productHref(HANDLES.kit_suzuki)
    },
    triumph: {
      title: 'Triumph Motorcycle Tool Set (SKU 52504-TRI), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_triumph), pro: productHref(HANDLES.pro_triumph)
    },
    voge: {
      title: 'VOGE Motorcycle Tool Set (SKU 52504-VOGE), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_voge), pro: productHref(HANDLES.pro_voge)
    },
    yamaha: {
      title: 'Yamaha Motorcycle Tool Set (SKU 52504-YAM), $349.',
      body: 'The Primary Tool Set and your bike\'s Add-On Pouch, in one roll. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref(HANDLES.kit_yamaha), pro: productHref(HANDLES.pro_yamaha)
    }
  };

  /*
   * Per-MODEL results, keyed by the exact model label in MODELS. Checked
   * before RESULTS[make], so a group only needs entries where its models do
   * NOT share one kit.
   *
   * Only the 'honda' group needs them, and it needs them badly: it lists
   * seven brands whose kits are seven different products. Sending all of
   * them to the Honda kit gave a Triumph rider a kit without the 24mm socket
   * (52527) their own row specifies, and a CRF rider a kit built on the
   * wrong Add-On entirely (52534 instead of 52583).
   *
   * Every kit and Pro Pack below is published and in stock. Composition from
   * the Bundle Composition tab of the SBVTools 2026 MRP USA MAP Policy sheet.
   */
  var MODEL_RESULTS = {
    'Pan America 1250/Special/ST': {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The Revolution Max bikes take their own kit, built on a different Add-On from the other Harleys. That Add-On is out of stock, so this is the Primary Tool Set it builds on.',
      cta: productHref('sbv-basic-tool-set')
    },
    'Sportster S': {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The Revolution Max bikes take their own kit, built on a different Add-On from the other Harleys. That Add-On is out of stock, so this is the Primary Tool Set it builds on.',
      cta: productHref('sbv-basic-tool-set')
    },
    'Nightster/Special': {
      title: 'Primary Tool Set (SKU 52501), $239.',
      body: 'The Revolution Max bikes take their own kit, built on a different Add-On from the other Harleys. That Add-On is out of stock, so this is the Primary Tool Set it builds on.',
      cta: productHref('sbv-basic-tool-set')
    },
    'CRF250L/CRF250L Rally': {
      title: 'Honda CRF250, 300L/Rally Motorcycle Tool Set (SKU 52603-CRF), $349.',
      body: 'The CRF takes its own kit, built on a different Add-On from the other Hondas. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref('sbv-honda-crf-tool-set'), pro: productHref('sbv-honda-crf-pro-pack')
    },
    'CRF300L/CRF300L Rally': {
      title: 'Honda CRF250, 300L/Rally Motorcycle Tool Set (SKU 52603-CRF), $349.',
      body: 'The CRF takes its own kit, built on a different Add-On from the other Hondas. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref('sbv-honda-crf-tool-set'), pro: productHref('sbv-honda-crf-pro-pack')
    },
    'CRF450L/CRF450RL': {
      title: 'Honda CRF250, 300L/Rally Motorcycle Tool Set (SKU 52603-CRF), $349.',
      body: 'The CRF takes its own kit, built on a different Add-On from the other Hondas. Or step up to the Pro Pack with the 3/8" Digital Torque Adapter.',
      cta: productHref('sbv-honda-crf-tool-set'), pro: productHref('sbv-honda-crf-pro-pack')
    }
  };

  /*
   * Populate a Make <select> with the canonical MAKES list.
   * Safe to call repeatedly.
   */
  function populateMakeSelect(selectEl) {
    if (!selectEl) return;
    selectEl.innerHTML = MAKES.map(function (m) {
      return '<option value="' + escapeAttr(m.value) + '">' + escapeHtml(m.label) + '</option>';
    }).join('');
  }

  /*
   * Populate a Model <select> based on the chosen make slug.
   * Empty or unknown make shows the "Pick a make first" placeholder.
   */
  function populateModelSelect(modelEl, makeValue) {
    if (!modelEl) return;
    var models = MODELS[makeValue] || [];
    if (!models.length) {
      modelEl.innerHTML = '<option value="">Pick a make first</option>';
      modelEl.disabled = true;
      return;
    }
    modelEl.disabled = false;
    modelEl.innerHTML = '<option value="">Select Model</option>' +
      models.map(function (m) {
        return '<option value="' + escapeAttr(m) + '">' + escapeHtml(m) + '</option>';
      }).join('');
  }

  /*
   * Wire a Make/Model pair so the Model select updates whenever the
   * Make select changes.
   */
  function wireCascade(makeEl, modelEl) {
    if (!makeEl || !modelEl) return;
    makeEl.addEventListener('change', function () {
      populateModelSelect(modelEl, makeEl.value);
    });
  }

  /*
   * Read ?make=...&model=... from the URL and pre-fill the two selects.
   * Returns true if a make was found and applied. Accepts slug values
   * (make=bmw) and human labels (make=BMW), so the hero form can submit
   * either and the finder still resolves.
   */
  function prefillFromQuery(makeEl, modelEl) {
    if (!makeEl || !modelEl) return false;
    var params = new URLSearchParams(window.location.search);
    var rawMake = (params.get('make') || '').trim();
    var rawModel = (params.get('model') || '').trim();
    if (!rawMake) return false;

    var slug = resolveMakeSlug(rawMake);
    if (!slug) return false;

    makeEl.value = slug;
    populateModelSelect(modelEl, slug);

    if (rawModel) {
      var options = Array.prototype.slice.call(modelEl.options);
      var exact = options.find(function (o) { return o.value === rawModel; });
      if (exact) {
        modelEl.value = rawModel;
      } else {
        var loose = options.find(function (o) {
          return o.value && o.value.toLowerCase() === rawModel.toLowerCase();
        });
        if (loose) modelEl.value = loose.value;
      }
    }
    return true;
  }

  /*
   * Map a human-readable Make name (or already-a-slug) to the slug key.
   * Returns null if no match. Case-insensitive.
   */
  function resolveMakeSlug(input) {
    if (!input) return null;
    var s = String(input).trim().toLowerCase();
    if (MODELS[s]) return s;
    var aliases = {
      'bmw': 'bmw',
      'harley-davidson': 'harley',
      'harley davidson': 'harley',
      'harley': 'harley',
      'hd': 'harley',
      'ktm': 'ktm',
      'husqvarna': 'husqvarna',
      'gasgas': 'gasgas',
      'gas gas': 'gasgas',
      'ducati': 'ducati',
      'honda': 'honda',
      'kawasaki': 'kawasaki',
      'yamaha': 'yamaha',
      'triumph': 'triumph',
      'ajp': 'ajp',
      'aprilia': 'aprilia',
      'royal enfield': 'royalenfield',
      'royal-enfield': 'royalenfield',
      're': 'royalenfield',
      'suzuki': 'suzuki',
      'pan america': 'harley',
      'indian': 'indian',
      'cfmoto': 'cfmoto',
      'cf moto': 'cfmoto',
      'kove': 'kove',
      'voge': 'voge',
      're_hdpa': 'royalenfield',
      'multi-brand': 'expert',
      'expert': 'expert'
    };
    return aliases[s] || null;
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  // Public surface.
  global.SBV_FYB = {
    HANDLES: HANDLES,
    MAKES: MAKES,
    HERO_MAKES: HERO_MAKES,
    MODELS: MODELS,
    RESULTS: RESULTS,
    MODEL_RESULTS: MODEL_RESULTS,
    productHref: productHref,
    populateMakeSelect: populateMakeSelect,
    populateModelSelect: populateModelSelect,
    wireCascade: wireCascade,
    prefillFromQuery: prefillFromQuery,
    resolveMakeSlug: resolveMakeSlug,
    escapeHtml: escapeHtml,
    escapeAttr: escapeAttr
  };
})(window);
